/**
 * st-draggable-carousel.js
 *
 * Reusable Draggable Slider Class
 * Usage: new DraggableSlider(containerElOrSelector, options)
 *
 * Notes for Shopify:
 * - We auto-init any element with [data-slider]
 * - Safe re-initialization in Theme Editor via shopify:section:* events
 * - Guard prevents double-inits (dataset.sliderInited)
 */

class DraggableSlider {
  constructor(containerSelector, options = {}) {
    // Default options
    this.options = {
      carouselSelector: '[data-carousel]',
      prevBtnSelector: '[data-prev]',
      nextBtnSelector: '[data-next]',
      itemsPerViewDesktop: 3,
      itemsPerViewMobile: 1,
      breakpoint: 768,
      autoScroll: true,
      autoScrollInterval: 5000,
      dragThreshold: 50,
      enableLoop: false,
      pauseOnHover: true,
      transitionDuration: '0.3s',
      activationWidth: null,
      verticalScrollThreshold: 10,
      continuous: false,
      continuousSpeed: 30,
      enableDrag: true,
      ...options
    };

    // Handle both DOM element and selector string
    this.container = typeof containerSelector === 'string'
      ? document.querySelector(containerSelector)
      : containerSelector;

    if (!this.container) {
      console.warn(`DraggableSlider: Container "${containerSelector}" not found`);
      return;
    }

    if (this.container.dataset.width) {
      this.options.activationWidth = parseInt(this.container.dataset.width, 10);
    }

    this.carousel = this.container.querySelector(this.options.carouselSelector);
    this.prevBtn = this.container.querySelector(this.options.prevBtnSelector);
    this.nextBtn = this.container.querySelector(this.options.nextBtnSelector);

    if (!this.carousel) {
      console.warn(`DraggableSlider: Carousel not found in container`, this.container);
      return;
    }

    // State
    this.currentIndex = 0;
    this.totalItems = this.carousel.children.length;
    this.itemsPerView = this.getItemsPerView();
    this.maxIndex = this.getMaxIndex();
    this.isActive = false;
    this.isInitialized = false;

    // Continuous scroll state
    this.continuousOffset = 0;
    this.continuousAnimationId = null;
    this.lastTimestamp = null;
    this.isPaused = false;
    this.clonedItems = [];

    // Drag state
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.initialTransform = 0;
    this.animationFrame = null;
    this.autoScrollTimer = null;
    this.isHorizontalSwipe = null;

    this.originalStyles = {
      transform: this.carousel.style.transform,
      transition: this.carousel.style.transition,
      cursor: this.carousel.style.cursor
    };

    this.init();
  }

  init() {
    if (this.options.continuous) {
      this.setupContinuousScroll();
    } else {
      this.checkActivation();
      this.bindResizeListener();
    }
  }

  setupContinuousScroll() {
    // Always active in continuous mode
    this.isActive = true;

    // Clone items for seamless loop
    this.cloneItemsForContinuous();

    // Styles
    this.carousel.style.display = 'flex';
    this.carousel.style.transition = 'none';
    if (this.options.enableDrag) {
      this.carousel.style.cursor = 'grab';
    }

    // Events
    this.bindContinuousEvents();

    // Start loop
    this.startContinuousScroll();

    // Hide nav in continuous mode
    if (this.prevBtn) this.prevBtn.style.display = 'none';
    if (this.nextBtn) this.nextBtn.style.display = 'none';
  }

  cloneItemsForContinuous() {
    // Clear existing clones
    this.clonedItems.forEach(clone => clone.remove());
    this.clonedItems = [];

    const originalItems = Array.from(this.carousel.children);

    // Calculate sets needed (fill at least 2x viewport)
    const containerWidth = this.carousel.offsetWidth || this.container.offsetWidth || window.innerWidth;
    let totalWidth = 0;
    originalItems.forEach(item => (totalWidth += item.offsetWidth));

    // Guard against zero-width (e.g., hidden in editor)
    if (totalWidth === 0) {
      // Try again on next frame
      requestAnimationFrame(() => this.cloneItemsForContinuous());
      return;
    }

    const setsNeeded = Math.ceil((containerWidth * 2) / totalWidth) + 1;

    for (let i = 0; i < setsNeeded; i++) {
      originalItems.forEach(item => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-clone', 'true');
        this.carousel.appendChild(clone);
        this.clonedItems.push(clone);
      });
    }
  }

  startContinuousScroll() {
    if (this.continuousAnimationId) {
      cancelAnimationFrame(this.continuousAnimationId);
    }

    const originalItemsWidth = this.getOriginalItemsWidth();

    const animate = (timestamp) => {
      if (!this.lastTimestamp) this.lastTimestamp = timestamp;
      const delta = timestamp - this.lastTimestamp;
      this.lastTimestamp = timestamp;

      if (!this.isPaused && !this.isDragging) {
        const movement = (this.options.continuousSpeed * delta) / 1000; // px per second
        this.continuousOffset -= movement;

        if (Math.abs(this.continuousOffset) >= originalItemsWidth) {
          this.continuousOffset += originalItemsWidth;
        }

        this.carousel.style.transform = `translateX(${this.continuousOffset}px)`;
      }

      this.continuousAnimationId = requestAnimationFrame(animate);
    };

    this.continuousAnimationId = requestAnimationFrame(animate);
  }

  checkActivation() {
    const shouldBeActive = this.shouldActivateSlider();
    if (shouldBeActive && !this.isActive) {
      this.activate();
    } else if (!shouldBeActive && this.isActive) {
      this.deactivate();
    }
  }

  getOriginalItemsWidth() {
    let width = 0;
    let marginLeft = 0;
    const children = Array.from(this.carousel.children);

    for (let child of children) {
      if (!child.hasAttribute('data-clone')) {
        const cs = window.getComputedStyle(child);
        marginLeft = parseFloat(cs.marginLeft) || 0;
        const marginRight = parseFloat(cs.marginRight) || 0;
        width += child.offsetWidth + marginLeft + marginRight;
      } else {
        break; // stop at first clone
      }
    }
    return width + marginLeft;
  }

  bindContinuousEvents() {
    // Drag
    if (this.options.enableDrag) {
      this.carousel.addEventListener('mousedown', this.handleContinuousStart);
      document.addEventListener('mousemove', this.handleContinuousMove);
      document.addEventListener('mouseup', this.handleContinuousEnd);

      this.carousel.addEventListener('touchstart', this.handleContinuousStart);
      document.addEventListener('touchmove', this.handleContinuousMove);
      document.addEventListener('touchend', this.handleContinuousEnd);
    }

    // Pause on hover
    if (this.options.pauseOnHover) {
      this.container.addEventListener('mouseenter', () => { this.isPaused = true; });
      this.container.addEventListener('mouseleave', () => {
        if (!this.isDragging) this.isPaused = false;
      });
    }

    // Click guard
    this.bindClickableItems();

    // Resize
    window.addEventListener('resize', this.handleContinuousResize);
  }

  handleContinuousStart = (e) => {
    if (!this.options.enableDrag) return;
    this.isDragging = true;
    this.isPaused = true;
    this.startX = this.getEventX(e);
    this.startY = this.getEventY(e);
    this.initialTransform = this.continuousOffset;
    this.isHorizontalSwipe = null;
    this.carousel.style.cursor = 'grabbing';
    if (e.type.includes('mouse')) e.preventDefault();
  };

  handleContinuousMove = (e) => {
    if (!this.isDragging || !this.options.enableDrag) return;

    const currentX = this.getEventX(e);
    const currentY = this.getEventY(e);
    const deltaX = currentX - this.startX;
    const deltaY = currentY - this.startY;

    if (this.isHorizontalSwipe === null && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
      this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    }
    if (this.isHorizontalSwipe === false) return;

    if (this.isHorizontalSwipe === true) {
      e.preventDefault();
      this.continuousOffset = this.initialTransform + deltaX;

      const originalItemsWidth = this.getOriginalItemsWidth();
      if (Math.abs(this.continuousOffset) >= originalItemsWidth) {
        this.continuousOffset = this.continuousOffset % originalItemsWidth;
        this.initialTransform = this.continuousOffset - deltaX;
      }
      this.carousel.style.transform = `translateX(${this.continuousOffset}px)`;
    }
  };

  handleContinuousEnd = () => {
    if (!this.isDragging || !this.options.enableDrag) return;
    this.isDragging = false;
    this.isPaused = false;
    this.carousel.style.cursor = 'grab';
    this.isHorizontalSwipe = null;
    this.lastTimestamp = null; // smooth resume
  };

  handleContinuousResize = () => {
    this.cloneItemsForContinuous();
  };

  shouldActivateSlider() {
    if (this.options.activationWidth === null) return true;
    return window.innerWidth <= this.options.activationWidth;
  }

  activate() {
    if (this.isActive) return;
    this.isActive = true;

    this.setupStyles();
    this.bindEvents();
    this.isInitialized = true;

    this.updateCarousel();
    this.toggleSliderElements(true);

    if (this.options.autoScroll && this.totalItems > this.itemsPerView) {
      this.startAutoScroll();
    }
  }

  deactivate() {
    if (!this.isActive) return;
    this.isActive = false;

    this.stopAutoScroll();

    this.carousel.style.transform = this.originalStyles.transform || '';
    this.carousel.style.transition = this.originalStyles.transition || '';
    this.carousel.style.cursor = this.originalStyles.cursor || '';

    this.currentIndex = 0;
    this.toggleSliderElements(false);
  }

  toggleSliderElements(show) {
    const dots = this.container.querySelectorAll('.slider-dot, .slider-dots');
    dots.forEach(dot => { dot.style.display = show ? '' : 'none'; });

    if (this.prevBtn) this.prevBtn.style.display = show ? '' : 'none';
    if (this.nextBtn) this.nextBtn.style.display = show ? '' : 'none';

    if (show) {
      this.container.classList.add('slider-active');
      this.carousel.classList.add('slider-carousel');
    } else {
      this.container.classList.remove('slider-active');
      this.carousel.classList.remove('slider-carousel');
    }
  }

  setupStyles() {
    this.carousel.style.cursor = 'grab';
    this.carousel.style.transition = `transform ${this.options.transitionDuration} ease-in-out`;
  }

  getItemsPerView() {
    return window.innerWidth >= this.options.breakpoint
      ? this.options.itemsPerViewDesktop
      : this.options.itemsPerViewMobile;
  }

  getMaxIndex() {
    return Math.max(0, this.totalItems - this.itemsPerView);
  }

  updateCarousel(smooth = true) {
    const translateX = -(this.currentIndex * (100 / this.itemsPerView));

    this.carousel.style.transition = smooth
      ? `transform ${this.options.transitionDuration} ease-in-out`
      : 'none';

    this.carousel.style.transform = `translateX(${translateX}%)`;
    this.updateButtonStates();
  }

  updateButtonStates() {
    if (this.prevBtn) {
      this.prevBtn.style.opacity = this.currentIndex === 0 ? '0.5' : '1';
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.style.opacity = this.currentIndex >= this.maxIndex ? '0.5' : '1';
      this.nextBtn.disabled = this.currentIndex >= this.maxIndex;
    }
  }

  goToPrev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updateCarousel();
    } else if (this.options.enableLoop) {
      this.currentIndex = this.maxIndex;
      this.updateCarousel();
    }
  }

  goToNext() {
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex++;
      this.updateCarousel();
    } else if (this.options.enableLoop) {
      this.currentIndex = 0;
      this.updateCarousel();
    }
  }

  goToSlide(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
    this.updateCarousel();
  }

  getEventX(e) {
    return e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
  }

  getEventY(e) {
    return e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;
  }

  handleStart = (e) => {
    this.isDragging = true;
    this.startX = this.getEventX(e);
    this.startY = this.getEventY(e);
    this.currentX = this.startX;
    this.currentY = this.startY;
    this.isHorizontalSwipe = null;

    const matrix = new DOMMatrix(getComputedStyle(this.carousel).transform);
    this.initialTransform = matrix.m41;

    this.carousel.style.transition = 'none';
    this.carousel.style.cursor = 'grabbing';

    this.stopAutoScroll();

    if (e.type.includes('mouse')) e.preventDefault();
  };

  handleMove = (e) => {
    if (!this.isDragging) return;

    this.currentX = this.getEventX(e);
    this.currentY = this.getEventY(e);

    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;

    if (this.isHorizontalSwipe === null && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
      this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    }

    if (this.isHorizontalSwipe === false) return;

    if (this.isHorizontalSwipe === true) {
      e.preventDefault();

      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);

      this.animationFrame = requestAnimationFrame(() => {
        let newTransform = this.initialTransform + deltaX;

        const maxTransform = 0;
        const minTransform = -(this.maxIndex * (this.carousel.offsetWidth / this.itemsPerView));

        if (newTransform > maxTransform) {
          newTransform = maxTransform + (newTransform - maxTransform) * 0.3;
        } else if (newTransform < minTransform) {
          newTransform = minTransform + (newTransform - minTransform) * 0.3;
        }

        this.carousel.style.transform = `translateX(${newTransform}px)`;
      });
    }
  };

  handleEnd = () => {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.carousel.style.cursor = 'grab';

    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;

    if (this.isHorizontalSwipe === true) {
      if (Math.abs(deltaX) > this.options.dragThreshold) {
        if (deltaX > 0) this.goToPrev();
        else this.goToNext();
      } else {
        this.updateCarousel(true);
      }
    } else {
      this.updateCarousel(true);
    }

    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.isHorizontalSwipe = null;

    if (this.options.autoScroll && this.totalItems > this.itemsPerView) {
      setTimeout(() => this.startAutoScroll(), 1000);
    }
  };

  handleResize = () => {
    const newItemsPerView = this.getItemsPerView();
    const newMaxIndex = this.getMaxIndex();

    if (newItemsPerView !== this.itemsPerView) {
      this.itemsPerView = newItemsPerView;
      this.maxIndex = newMaxIndex;
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
      this.updateCarousel();
    }
  };

  bindResizeListener() {
    window.addEventListener('resize', this.handleResize);
  }

  bindEvents() {
    // Buttons
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.goToPrev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.goToNext());

    // Drag
    this.carousel.addEventListener('mousedown', this.handleStart);
    document.addEventListener('mousemove', this.handleMove);
    document.addEventListener('mouseup', this.handleEnd);

    this.carousel.addEventListener('touchstart', this.handleStart);
    document.addEventListener('touchmove', this.handleMove);
    document.addEventListener('touchend', this.handleEnd);

    // Disable context menu
    this.carousel.addEventListener('contextmenu', e => e.preventDefault());

    // Resize
    window.addEventListener('resize', this.handleResize);

    // Pause on hover
    if (this.options.pauseOnHover) {
      this.container.addEventListener('mouseenter', () => this.stopAutoScroll());
      this.container.addEventListener('mouseleave', () => {
        if (this.options.autoScroll && this.totalItems > this.itemsPerView) this.startAutoScroll();
      });
    }

    // Click guard
    this.bindClickableItems();
  }

  bindClickableItems() {
    const clickableItems = this.carousel.querySelectorAll('a, [data-href], [onclick]');

    clickableItems.forEach(item => {
      let hasDragged = false;

      // Extract target for non-anchors
      let targetUrl = '';
      if (item.tagName === 'A') {
        targetUrl = item.href;
      } else if (item.dataset.href) {
        targetUrl = item.dataset.href;
      } else if (item.onclick) {
        const onclickStr = item.onclick.toString();
        const urlMatch = onclickStr.match(/window\.location\.href\s*=\s*['"`]([^'"`]+)['"`]/);
        if (urlMatch) {
          targetUrl = urlMatch[1];
          item.dataset.href = urlMatch[1];
        }
        item.removeAttribute('onclick');
      }

      const resetDragState = () => { hasDragged = false; };
      const setDragState = () => {
        if (this.isDragging && this.isHorizontalSwipe === true) hasDragged = true;
      };

      // Mouse
      item.addEventListener('mousedown', resetDragState);
      item.addEventListener('mousemove', setDragState);

      // Touch
      item.addEventListener('touchstart', resetDragState);
      item.addEventListener('touchmove', setDragState);

      item.addEventListener('click', (e) => {
        if (hasDragged) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        if (item.tagName !== 'A' && targetUrl) {
          e.preventDefault();
          window.location.href = targetUrl;
        }
      });
    });
  }

  startAutoScroll() {
    this.stopAutoScroll();
    // Uncomment if you want timed auto-advance in non-continuous mode:
    // if (this.totalItems <= this.itemsPerView) return;
    // this.autoScrollTimer = setInterval(() => {
    //   if (!this.isDragging) {
    //     if (this.currentIndex < this.maxIndex) {
    //       this.goToNext();
    //     } else if (this.options.enableLoop) {
    //       this.goToSlide(0);
    //     } else {
    //       this.stopAutoScroll();
    //     }
    //   }
    // }, this.options.autoScrollInterval);
  }

  stopAutoScroll() {
    if (this.autoScrollTimer) {
      clearInterval(this.autoScrollTimer);
      this.autoScrollTimer = null;
    }
  }

  destroy() {
    if (this.continuousAnimationId) {
      cancelAnimationFrame(this.continuousAnimationId);
    }

    this.stopAutoScroll();
    this.clonedItems.forEach(clone => clone.remove());

    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.goToPrev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.goToNext);

    this.carousel.removeEventListener('mousedown', this.handleStart);
    document.removeEventListener('mousemove', this.handleMove);
    document.removeEventListener('mouseup', this.handleEnd);

    this.carousel.removeEventListener('touchstart', this.handleStart);
    document.removeEventListener('touchmove', this.handleMove);
    document.removeEventListener('touchend', this.handleEnd);

    window.removeEventListener('resize', this.handleResize);

    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
  }

  // Public API
  getCurrentIndex() { return this.currentIndex; }
  getTotalSlides() { return Math.ceil(this.totalItems / this.itemsPerView); }
  refresh() {
    this.totalItems = this.carousel.children.length;
    this.itemsPerView = this.getItemsPerView();
    this.maxIndex = this.getMaxIndex();
    this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
    this.updateCarousel();
  }
  pauseContinuous() {
    this.isPaused = true;
  }
  resumeContinuous() {
    this.isPaused = false;
    this.lastTimestamp = null;
  }
  setContinuousSpeed(speed) {
    this.options.continuousSpeed = speed;
  }
}

/* ---------- Auto-init & Shopify Section Hooks ---------- */

function initSliders(root = document) {
  root.querySelectorAll('[data-slider]').forEach((container, index) => {
    // Avoid double-initialization
    if (container.dataset.sliderInited === 'true') return;

    const options = {};
    if (container.dataset.itemsDesktop) options.itemsPerViewDesktop = parseInt(container.dataset.itemsDesktop, 10);
    if (container.dataset.itemsMobile) options.itemsPerViewMobile = parseInt(container.dataset.itemsMobile, 10);
    if (container.dataset.autoScroll === 'false') options.autoScroll = false;
    if (container.dataset.loop === 'true') options.enableLoop = true;
    if (container.dataset.autoScrollInterval) options.autoScrollInterval = parseInt(container.dataset.autoScrollInterval, 10);
    if (container.dataset.continuous === 'true') options.continuous = true;
    if (container.dataset.continuousSpeed) options.continuousSpeed = parseInt(container.dataset.continuousSpeed, 10);
    if (container.dataset.enableDrag === 'false') options.enableDrag = false;

    if (!container.id) container.id = `slider-${index}`;
    new DraggableSlider(container, options);

    // mark as initialized
    container.dataset.sliderInited = 'true';
  });
}

// Initial page load
document.addEventListener('DOMContentLoaded', () => initSliders());

// Shopify Theme Editor — sections are inserted/re-rendered dynamically
document.addEventListener('shopify:section:load', (e) => initSliders(e.target));
document.addEventListener('shopify:section:reorder', (e) => initSliders(e.target));
document.addEventListener('shopify:block:select', (e) => initSliders(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  // Optional: if you store instances, destroy them here.
  // This implementation relies on the init guard to prevent duplicates.
});